import React from 'react';
import { connect } from 'react-redux';
import TooltipElement from '../components/tooltipElement';
import TooltipButtonGroup from '../components/tooltipButtonGroup';
import OverlayLoader from '../components/overlayLoader';
import TableSelectionManager from '../components/tableSelectionManager';
import setTitleToFilters from '../common/filtersTitles';
import ModalMessage from './modalMessage';

import './common.css';
import AccountsTable from '../components/accountsTable';
import AccountsAddDialog from '../components/accountsAddDialog';
import * as accountsActions from '../store/accountsSlice';
import { accountsSelectors } from '../store/accountsSlice';
import { showModalMessage } from '../store/modalsSlice';
import AccountsHistoryDialog from './accountsHistoryDialog';
import AccountEditDialog from './accountEditDialog';
import { unwrapResult } from '@reduxjs/toolkit';
import * as objectDataActions from '../store/objectDataSlice';
import { objectDataNames, objectDataSelectors } from '../store/objectDataSlice';

const thatObjectName = 'accounts';

class AccountsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAccountsAddDialog: false,
      showHistoryDialog: false,
      showAccountEditDialog: false,
      accountEditDialogMode: AccountEditDialog.Mode.Create,
    };
  }

  componentDidMount() {
    setTitleToFilters();
    this.props.fetchAllAccounts();
    this.props.fetchAccountsProviders();
  }

  render() {
    return (
      <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
        <div>
          <h1>AI accounts</h1>
          {this.renderButtons()}
          {this.renderContent()}
          {this.renderModalMessage()}
          {this.renderAddAccountsDialog()}
          {this.renderHistoryDialog()}
          {this.renderAccountEditDialog()}
        </div>
      </OverlayLoader>
    );
  }

  showAccountsHistory = () => {
    this.setState({ showHistoryDialog: true });
  };

  isHistoryButtonDisabled = () => this.props.selectedAccounts.length !== 1;

  renderModalMessage = () => <ModalMessage />;

  renderButtons = () => (
    <TooltipButtonGroup>
      <TooltipElement type="button" text="Refresh" icon="refresh" onClick={this.refreshAccounts} />
      <TooltipElement type="button" text="Add accounts" icon="plus" onClick={this.addAccounts} displayOnlyForWriter />
      <TooltipElement type="button" text="Show AI accounts history" icon="list-alt" onClick={this.showAccountsHistory} disabled={this.isHistoryButtonDisabled()} />
      <TooltipElement type="button" text="Create account" icon="plus-sign" onClick={this.createAccount} displayOnlyForWriter/>
    </TooltipButtonGroup>
  );

  // getActionsForFavorites = () => ({
  //   addToFavorites: this.props.addAccountsToFavorites,
  //   removeFromFavorites: this.props.removeAccountsFromFavorites,
  // });

  renderAddAccountsDialog = () => {
    return (
      <AccountsAddDialog
        show={this.state.showAccountsAddDialog}
        onClose={this.closeAccountsAddDialog}
        onUpload={this.onUpload}
        title="Add AI accounts"
        isLoading={this.props.isAccountsAdding}
        accept=".csv"
        maxFilesCount={1}
        providersState={this.props.providersState}
      />
    )
  }

  renderHistoryDialog = () => {
    return (
      <AccountsHistoryDialog
        show={this.state.showHistoryDialog}
        onClose={this.closeHistoryDialog}
        id={this.getSelectedAccountFieldValue('id')}
        login={this.getSelectedAccountFieldValue('login')}
      />
    );
  };

  renderAccountEditDialog = () => (
    <AccountEditDialog 
      mode={this.state.accountEditDialogMode}
      show={this.state.showAccountEditDialog}
      onClose={this.closeAccountEditDialog}
      providersState={this.props.providersState}
    />
  );

  onUpload = (files, providerId) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('accounts_files[]', file);
    }
    formData.append('provider_id', providerId);
    const promise = this.props.postAccounts({ formData });
    promise
      .then(unwrapResult)
      .then((payload) => {
        this.props.fetchAllAccounts();
        this.props.showModalMessage('Accounts loading results', `
          Added new accounts: ${payload.inserted}
          Edited accounts: ${payload.updated}
          Ignored accounts: ${payload.ignored}
        `);
      })
      .catch(() => {
        this.props.showModalMessage('Error', this.props.accountsAddingError.toString());
      })
      .finally(this.closeAccountsAddDialog)
  }

  renderContent = () => {
    if (this.props.accountsFetchingError) return this.renderError();
    return (
      <TableSelectionManager
        items={this.props.accounts}
        objectName={thatObjectName}
      >
        <AccountsTable
          accounts={this.props.accounts}
          handleLoginClick={this.handleLoginClick}
          requestData={this.props.requestData}
          providers={this.props.providersState.data}
          // actions={this.getActionsForFavorites()}
        />
      </TableSelectionManager>
    );
  };

  handleLoginClick = (account) => (e) => {
    if (e.ctrlKey || e.metaKey) {
      const { login, id } = account;
      this.props.showModalMessage('Loading...', null);
      this.props.fetchAccountPassword({ id, login })
        .then(unwrapResult)
        .then(() => {
          this.props.showModalMessage(
            'Password',
            this.props.selectedAccountPassword,
            { onHide: this.props.clearAccountPassword },
          );
        })
        .catch((e) => {
          this.props.showModalMessage(
            'Error',
            `Password fetch failed, try again\n${e.toString()}`,
          );
        })
    }
  }

  getSelectedAccountFieldValue = (fieldName, defaultValue = null) => {
    const account = this.props.selectedAccounts[0];
    if (!account) return defaultValue;
    try {
      return fieldName.split('.').reduce((acc, key) => acc[key], account);
    } catch (e) {
      return defaultValue;
    }
  };

  closeHistoryDialog = () => {
    this.setState({ showHistoryDialog: false })
  }

  renderError = () => (
    <p className="text-danger">Error: {this.props.accountsFetchingError.toString()}</p>
  );

  isLoading = () => this.props.isAccountsFetching;

  getLoaderMessage = () => {
    if (this.props.isAccountsFetching) {
      return "Accounts loading...";
    }
  };

  refreshAccounts = () => {
    this.props.fetchAllAccounts();
  };

  addAccounts = () => {
    this.setState({
      showAccountsAddDialog: true,
    });
  };

  closeAccountsAddDialog = () => {
    this.setState({ showAccountsAddDialog: false });
  };

  createAccount = () => {
    this.setState({
      accountEditDialogMode: AccountEditDialog.Mode.Create,
      showAccountEditDialog: true,
    });
  }

  closeAccountEditDialog = () => {
    this.setState({ showAccountEditDialog: false });
  }

}

const mapStateToProps = (state) => {
  return {
    accounts: accountsSelectors.selectAll(state),
    selectedAccountPassword: accountsSelectors.selectedAccountPassword(state),
    isAccountsFetching: accountsSelectors.isFetching(state),
    isAccountsAdding: accountsSelectors.isAdding(state),
    accountsFetchingError: accountsSelectors.fetchingError(state),
    accountsAddingError: accountsSelectors.addingError(state),
    selectedAccounts: accountsSelectors.selectedAccounts(state),

    providersState: objectDataSelectors.providersState(state),
  };
};

const actionCreators = {
  fetchAllAccounts: accountsActions.fetchAll,
  postAccounts: accountsActions.add,
  fetchAccountPassword: accountsActions.fetchPasswordByIdAndLogin,
  clearAccountPassword: accountsActions.clearAccountPassword,
  fetchAccountsProviders: () => objectDataActions.fetchObjectData({ objectName: objectDataNames.accountsProviders }),
  showModalMessage: (title, text, payload) => showModalMessage({ title, text, payload }),
};

export default connect(mapStateToProps, actionCreators)(AccountsContainer);



// WEBPACK FOOTER //
// ./src/containers/accountsContainer.js